import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {useCallback} from 'react';
import {useIntl} from 'react-intl';
import useAlerts from 'src/hooks/use-alerts';
import PermissionService from 'src/services/permission-service';

type FilterByPropertyId = {
  propertyId: number;
  ownerId?: never;
}

type FilterByOwnerId = {
  ownerId: number;
  propertyId?: never;
}

type Filter = {
  ownerClass: 'device';
  propertyClass: 'maintenance';
};

type FilterByOwnerIdOrPropertyId = Filter & (FilterByPropertyId | FilterByOwnerId);

type DeviceToMaintenancePermission = {
  deviceId: number;
  maintenanceId: number;
}

export const permissionKeys = {
  all: ['permissions'],
  byDeviceId: (deviceId: number) => ['permissions', deviceId],
  filters: ({ownerClass, propertyClass}: Filter) => [permissionKeys.all, 'filter', ownerClass, propertyClass],
  byOwnerIdOrPropertyId: ({ownerClass, propertyClass, ...rest}: FilterByOwnerIdOrPropertyId) => [...permissionKeys.filters({ownerClass, propertyClass}), rest],
};

export const usePermissions = (filter: Filter) => {
  const permissionService = new PermissionService();

  return useQuery({
    queryKey: permissionKeys.filters(filter),
    queryFn: () => permissionService.addPermissionsBulk(),
    initialData: [],
  });
};

export const usePermissionsByOwnerIdOrPropertyId = (filter: FilterByOwnerIdOrPropertyId) => {
  const permissionService = new PermissionService();

  return useQuery<unknown, unknown, DeviceToMaintenancePermission[]>({
    // @ts-ignore
    queryKey: permissionKeys.byOwnerIdOrPropertyId(filter),
    queryFn: () => permissionService.list(filter),
    initialData: [],
    select: (data: DeviceToMaintenancePermission[]) => data.map((item) => {
      // item is using database column names, so them are coming fully lowercase
      // we need to convert them to camelCase to match the TypeScript interface

      const owner = filter.ownerClass.toLowerCase() + 'Id';
      const property = filter.propertyClass.toLowerCase() + 'Id';

      return {
        // @ts-ignore
        [owner]: item.deviceid,
        // @ts-ignore
        [property]: item.maintenanceid,
      };
    }),
  });
};

export const useUpdateDeviceToMaintenancePermissionsBulk = () => {
  const queryClient = useQueryClient();
  const permissionService = new PermissionService();
  const intl = useIntl();
  const {toast} = useAlerts() as unknown as {toast: Record<string, (text: string) => void>};

  const createMutation = useMutation({
    mutationFn: (data: DeviceToMaintenancePermission[]) => permissionService.addPermissionsBulk(data),
  });

  const deleteMutation = useMutation({
    mutationFn: (data: DeviceToMaintenancePermission[]) => permissionService.removePermissionsBulk(data),
  });

  const mutate = useCallback((data: { created: DeviceToMaintenancePermission[], deleted: DeviceToMaintenancePermission[] }, options = {}) => {
    const {created, deleted} = data;

    deleteMutation.mutate(deleted, {
      onSuccess: () => {
        createMutation.mutate(created, {
          onSuccess: () => {
            toast.success(intl.formatMessage({id: 'permissionsUpdatedSuccess'}));
            options.onSuccess && options.onSuccess();
            queryClient.invalidateQueries({queryKey: permissionKeys.filters({ownerClass: 'device', propertyClass: 'maintenance'})});
          },
        });
      },
    });
  }, [createMutation, deleteMutation, queryClient, toast]);

  return {
    mutate,
    isPending: createMutation.isPending || deleteMutation.isPending,
  };
};
